import { router, WhenVisible } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"
import FirearmGrid from "./FirearmGrid"
import Pagination from "./Pagination"
import ListingSortControl from "./ListingSortControl"
import { DEFAULT_SORT, sortParamForPage, variantForPage } from "./listingSort"

const PROP_KEYS = {
    live_page: "liveAuctions",
    sold_page: "recentlySold",
}

const META_KEYS = {
    live_page: "liveMeta",
    sold_page: "soldMeta",
}

function GridSkeleton() {
    return (
        <div
            className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3"
            aria-hidden="true"
        >
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3 animate-pulse">
                    <div className="aspect-[4/3] w-full rounded-md bg-gray-200" />
                    <div className="w-3/4 h-4 bg-gray-200 rounded" />
                    <div className="w-1/2 h-4 bg-gray-200 rounded" />
                </div>
            ))}
        </div>
    )
}

export default function PaginatedFirearmGrid({
    items = [],
    meta,
    pageName = "live_page",
    mode = "paginate",
    title,
    subtitle,
    emptyMessage,
}) {
    const [hydrated, setHydrated] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const sortVariant = variantForPage(pageName)
    const sortParam = sortParamForPage(pageName)

    useEffect(() => {
        setHydrated(true)
    }, [])

    // The server orders the list from the URL query param (falling back to the
    // default) and echoes the resolved key back in meta. Mirror it locally so
    // the select reflects a new choice immediately while the reload is in
    // flight.
    const serverSort = meta?.sort ?? DEFAULT_SORT[sortVariant]
    const [sortValue, setSortValue] = useState(serverSort)

    useEffect(() => {
        setSortValue(serverSort)
    }, [serverSort])

    const propKey = PROP_KEYS[pageName] ?? PROP_KEYS.live_page
    const metaKey = META_KEYS[pageName] ?? META_KEYS.live_page

    // Navigate so the chosen sort lands in the URL (shareable / refresh-safe),
    // resetting this list to page one and dropping the accumulated merge so the
    // new ordering replaces it rather than appending.
    const applySort = (value) => {
        const params = new URLSearchParams(window.location.search)
        params.set(sortParam, value)
        params.delete(pageName)
        router.get(
            `${window.location.pathname}?${params.toString()}`,
            {},
            {
                only: [propKey, metaKey],
                reset: [propKey],
                preserveScroll: true,
                preserveState: true,
            }
        )
    }

    // Inertia merge props append each partial-reload page onto the running
    // list; under SSR the same page can be merged more than once, leaving the
    // same auction in the array twice. Dedupe by id so a listing never renders
    // a duplicate card regardless of how the merge resolved.
    const uniqueItems = useMemo(() => {
        const seen = new Set()
        return items.filter((item) => {
            if (item == null || seen.has(item.id)) {
                return false
            }
            seen.add(item.id)
            return true
        })
    }, [items])

    const currentPage = meta?.current_page ?? 1
    const lastPage = meta?.last_page ?? 1
    const hasMore = currentPage < lastPage

    // Ordering is server-side, so render in the order received. The sort param
    // rides along on every page load so each appended page stays consistent.
    const displayItems = uniqueItems

    const reloadParams = {
        only: [propKey, metaKey],
        data: { [pageName]: currentPage + 1, [sortParam]: sortValue },
        preserveScroll: true,
        preserveUrl: true,
    }

    const changeSort = (value) => {
        if (value === sortValue) {
            return
        }
        setSortValue(value)
        applySort(value)
    }

    const loadMore = () => {
        if (loadingMore || !hasMore) {
            return
        }
        router.reload({
            ...reloadParams,
            preserveState: true,
            onStart: () => setLoadingMore(true),
            onFinish: () => setLoadingMore(false),
        })
    }

    // The Load-more button / infinite-scroll trigger only take over once JS has
    // hydrated. Until then (SSR, no-JS, crawlers) and in explicit "paginate"
    // mode, the numbered <a href> links are the primary control.
    const enhanced = hydrated && (mode === "loadmore" || mode === "infinite")

    return (
        <div className="flex flex-col gap-6">
            {(title || subtitle) && (
                <header className="flex flex-col justify-between gap-4 md:items-center md:flex-row">
                    <div className="flex flex-col gap-1">
                        {title && (
                            <h2 className="text-xl font-bold text-gray-900">
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p className="text-sm text-gray-500">{subtitle}</p>
                        )}
                    </div>
                    {hydrated && items.length > 0 && (
                        <ListingSortControl
                            variant={sortVariant}
                            value={sortValue}
                            onChange={changeSort}
                        />
                    )}
                </header>
            )}

            <FirearmGrid items={displayItems} emptyMessage={emptyMessage} />

            {/*
                Always render the real numbered <a href="?…_page=N"> links so the
                paginated URLs stay crawlable regardless of SSR/JS. Once the
                enhanced UI takes over, keep them in the DOM but visually hidden
                (still keyboard/screen-reader navigable).
            */}
            <div className={enhanced ? "sr-only" : undefined}>
                <Pagination links={meta?.links} />
            </div>

            {enhanced && mode === "loadmore" && hasMore && (
                <div className="flex justify-center">
                    <button
                        type="button"
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="min-w-48 rounded-sm border border-[#800020] px-8 py-3 text-sm font-semibold text-[#800020] transition hover:bg-[#800020] hover:text-white disabled:opacity-50"
                    >
                        {loadingMore ? "Loading…" : "Load more"}
                    </button>
                </div>
            )}

            {enhanced && mode === "infinite" && hasMore && (
                <WhenVisible
                    always
                    buffer={400}
                    params={reloadParams}
                    fallback={<GridSkeleton />}
                >
                    <GridSkeleton />
                </WhenVisible>
            )}
        </div>
    )
}
